// React
import React, { useEffect, useRef } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// CSS
import "./VCenterScroll.css";

export interface VCenterScrollItem {
  text: string;
  isQuote?: boolean;
  icon?: IconProp;
  color?: string;
}

interface VCenterScrollProps {
  items: VCenterScrollItem[];
  snapDuration?: number; // ms, how slow the settle animation is
  snapIdleDelay?: number; // ms of no scrolling before it snaps
  wheelMultiplier?: number; // how much further a mouse wheel tick travels inside the snap zone
}

const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

const VCenterScroll: React.FC<VCenterScrollProps> = ({
  items,
  snapDuration = 900,
  snapIdleDelay = 150,
  wheelMultiplier = 1.5,
}) => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // tracks whether the current scroll motion is our own animation, so the
  // scroll events it generates don't get mistaken for user input
  const programmaticRef = useRef(false);

  useEffect(() => {
    let idleTimer: number | undefined;
    let animationFrame: number | undefined;
    let animating = false;

    const cancelAnimation = () => {
      animating = false;
      programmaticRef.current = false;
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
    };

    // finds the section whose center is nearest the viewport center, and
    // whether that section falls in the "snap zone": the bottom half of the
    // first item, all interior items, and the top half of the last item —
    // so scrolling onto/off the very start and end of the list stays free
    const locateClosest = () => {
      const viewportCenter = window.scrollY + window.innerHeight / 2;
      let closest: HTMLDivElement | null = null;
      let closestDistance = Infinity;
      let closestHeight = 0;
      let closestCenter = 0;
      let closestIndex = -1;
      const lastIndex = sectionRefs.current.length - 1;

      sectionRefs.current.forEach((el, index) => {
        if (!el) return;
        const elCenter = el.offsetTop + el.offsetHeight / 2;
        const distance = Math.abs(elCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = el;
          closestHeight = el.offsetHeight;
          closestCenter = elCenter;
          closestIndex = index;
        }
      });

      let edgeAllowed = true;
      if (closestIndex === 0) {
        // free while above the first item's own midpoint, snaps once past it
        edgeAllowed = viewportCenter >= closestCenter;
      } else if (closestIndex === lastIndex) {
        // snaps while above the last item's own midpoint, free once past it
        edgeAllowed = viewportCenter <= closestCenter;
      }

      const inSnapZone = closest !== null && edgeAllowed && closestDistance <= closestHeight / 2;

      return { closest, closestDistance, inSnapZone };
    };

    const snapToNearest = () => {
      const { closest, inSnapZone } = locateClosest();
      if (!closest || !inSnapZone) return;

      const target =
        (closest as HTMLDivElement).offsetTop +
        (closest as HTMLDivElement).offsetHeight / 2 -
        window.innerHeight / 2;
      const start = window.scrollY;
      const distanceToTravel = target - start;

      if (Math.abs(distanceToTravel) < 2) return;

      const startTime = performance.now();
      animating = true;
      programmaticRef.current = true;

      const step = (now: number) => {
        if (!animating) return;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / snapDuration, 1);
        window.scrollTo(0, start + distanceToTravel * easeInOutQuad(progress));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(step);
        } else {
          animating = false;
          programmaticRef.current = false;
        }
      };

      animationFrame = requestAnimationFrame(step);
    };

    const handleScroll = () => {
      // ignore scroll events produced by our own animation, otherwise it cancels itself
      if (programmaticRef.current) return;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(snapToNearest, snapIdleDelay);
    };

    // inside the snap zone, amplify each wheel tick so fewer of them are
    // needed to travel between items; outside it (first/last item), leave
    // wheel scrolling untouched
    const handleWheel = (e: WheelEvent) => {
      cancelAnimation();

      const { inSnapZone } = locateClosest();
      if (inSnapZone) {
        e.preventDefault();
        window.scrollBy(0, e.deltaY * wheelMultiplier);
      }

      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(snapToNearest, snapIdleDelay);
    };

    // real user touch input should interrupt an in-progress snap
    const handleTouchStart = () => cancelAnimation();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.clearTimeout(idleTimer);
      cancelAnimation();
    };
  }, [snapDuration, snapIdleDelay, wheelMultiplier]);

  return (
    <div className="vcenter-container">
      {items.map((item, index) => (
        <div
          key={index}
          className="vcenter-section"
          ref={(el) => (sectionRefs.current[index] = el)}
        >
          <div
            className="vcenter-card"
            style={item.color ? { borderLeftColor: item.color } : undefined}
          >
            {item.icon && (
              <FontAwesomeIcon icon={item.icon} size="2x" className="vcenter-icon" />
            )}
            <p className="vcenter-text">
              {item.isQuote ? `"${item.text}"` : item.text}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default VCenterScroll;
